/**
 * ProjectOutreachTab.tsx — Sprint 7E / 7.1D / 7.2 / 7.6B
 * Header alineado con Leads (DetailSection + ActionRow).
 * Sin hint input. Feed ASC (más viejo arriba, más nuevo abajo).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../ui/DetailSection.js';
import { SectionHeaderAction } from '../ui/SectionHeaderAction.js';
import { MessageComposer } from '../ui/MessageComposer.js';
import { EmptyState } from '../ui/EmptyState.js';
import { htmlToText } from '../outreach/utils.js';
import { LoadingState } from '../ui/LoadingState.js';
import {
  useProjectDrafts,
  useProjectReplies,
  useCreateManualDraft,
  type ProjectOutreachDraft,
  type ProjectOutreachReply,
  type ProjectClientEvent,
} from '../../hooks/useProjectOutreach.js';
import { ProjectDraftCardConnected } from './ProjectDraftCardConnected.js';
import { ProjectReplyCardConnected } from './ProjectReplyCardConnected.js';
import { ClientEventCard } from './ClientEventCard.js';
import type { ProjectRow } from '../../hooks/useProjects.js';

// ── Tipo unificado para la lista ──────────────────────────────────────────────

type FeedItem =
  | { kind: 'draft'; ts: number; item: ProjectOutreachDraft }
  | { kind: 'reply'; ts: number; item: ProjectOutreachReply }
  | { kind: 'clientEvent'; ts: number; item: ProjectClientEvent };

// ── Componente principal ──────────────────────────────────────────────────────

interface ProjectOutreachTabProps {
  project: ProjectRow;
}

export function ProjectOutreachTab({ project }: ProjectOutreachTabProps) {
  const { t } = useTranslation();
  const projectId = project.id;

  const { data: draftsData, isLoading: draftsLoading } = useProjectDrafts(projectId);
  const { data: repliesData, isLoading: repliesLoading } = useProjectReplies(projectId);

  const createDraft = useCreateManualDraft(projectId);

  // Inline manual composer state (New Email button)
  const [composerOpen, setComposerOpen]     = useState(false);
  const [composerSubject, setComposerSubject] = useState('');
  const [composerBody, setComposerBody]     = useState('');

  const isLoading = draftsLoading || repliesLoading;

  // Merge drafts + replies + client events — orden ASC (más viejo arriba, igual que Leads)
  const feed: FeedItem[] = [
    ...(draftsData?.drafts ?? []).map(d => ({ kind: 'draft' as const, ts: d.sentAt ?? d.createdAt, item: d })),
    ...(repliesData?.replies ?? []).map(r => ({ kind: 'reply' as const, ts: r.receivedAt, item: r })),
    ...(repliesData?.clientEvents ?? []).map(e => ({ kind: 'clientEvent' as const, ts: e.respondedAt, item: e })),
  ].sort((a, b) => a.ts - b.ts);

  const isEmpty = !isLoading && feed.length === 0;

  function handleCreateDraft() {
    const subj = composerSubject.trim();
    const body = htmlToText(composerBody);
    if (!subj || !body.trim()) return;
    createDraft.mutate(
      { subject: subj, bodyHtml: composerBody, bodyText: body },
      {
        onSuccess: () => {
          setComposerOpen(false);
          setComposerSubject('');
          setComposerBody('');
        },
      },
    );
  }

  return (
    <div style={{ maxWidth: '1200px', paddingTop: 'var(--evo-space-6)' }}>
      <DetailSection
        title={t('projects.outreach.title')}
        titleRight={
          <SectionHeaderAction
            onClick={() => setComposerOpen(prev => !prev)}
          >
            {t('projects.outreach.newEmail')}
          </SectionHeaderAction>
        }
        divider={false}
      >
        {/* Inline manual composer */}
        {composerOpen && (
          <div style={{
            marginBottom: 'var(--evo-space-3)',
            border: '1px solid var(--evo-stroke)',
            borderRadius: 'var(--evo-r-md)',
            padding: 'var(--evo-space-3) var(--evo-space-4)',
          }}>
            <MessageComposer
              showSubject
              subject={composerSubject}
              onSubjectChange={setComposerSubject}
              value={composerBody}
              onChange={setComposerBody}
              placeholder={t('projects.outreach.composing')}
              primaryLabel={t('projects.outreach.saveDraft')}
              loadingLabel={t('projects.outreach.generatingBtn')}
              onPrimary={() => handleCreateDraft()}
              onCancel={() => { setComposerOpen(false); setComposerSubject(''); setComposerBody(''); createDraft.reset(); }}
              loading={createDraft.isPending}
              error={createDraft.isError ? (createDraft.error?.message ?? t('projects.outreach.action_failed')) : null}
            />
          </div>
        )}

        {/* Feed — flujo natural, sin scroll interno */}
        {isLoading && <LoadingState />}
        {isEmpty && <EmptyState title={t('projects.outreach.empty')} />}
        {feed.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {feed.map(item =>
              item.kind === 'draft'
                ? <ProjectDraftCardConnected key={`d-${item.item.id}`} draft={item.item} projectId={projectId} />
                : item.kind === 'reply'
                ? <ProjectReplyCardConnected key={`r-${item.item.id}`} reply={item.item} projectId={projectId} />
                : <ClientEventCard key={`ce-${item.item.id}`} event={item.item} />,
            )}
          </div>
        )}
      </DetailSection>
    </div>
  );
}
